import React, { useEffect, useMemo, useState } from 'react';
import { Form, Input, Select, DatePicker, Row, Col, Alert, Button } from 'antd';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import CertBorrowApplicationView from '../CompanyCert/CertBorrowApplicationView';
import CompanyDeregistrationApplicationView from '../CompanyCert/CompanyDeregistrationApplicationView';
import SealApprovalAttachmentsView from '../Admin/SealApprovalAttachmentsView';
import { useHrOptions } from '../../hooks/useHrOptions';
import { PROBATION_STATUS, RESIGN_TYPE_OPTIONS } from '../../config/hrConstants';
import { resolveOnboardingEmployeeNo } from './copyProcessUtils';
import GroupedFinanceFormSnapshotView from './GroupedFinanceFormSnapshotView';
import FinanceProcessResubmitPanel from './FinanceProcessResubmitPanel';
import InternalFundTransferForm from '../ApprovalCenter/InternalFundTransferForm';
import BudgetAdjustmentForm from '../ApprovalCenter/BudgetAdjustmentForm';
import SocialFundPaymentForm from '../ApprovalCenter/SocialFundPaymentForm';
import SocialInsuranceChangeForm from '../ApprovalCenter/SocialInsuranceChangeForm';
import SocialFundPaymentReadonlyView from '../ApprovalCenter/SocialFundPaymentReadonlyView';
import SocialInsuranceChangeReadonlyView from '../ApprovalCenter/SocialInsuranceChangeReadonlyView';
import { GROUPED_FINANCE_SNAPSHOT_TYPES } from '../../utils/expenseFormSnapshotSections';
import {
  isSnapshotFileListItem,
  isSnapshotFullWidthItem,
  isSnapshotTextareaItem,
  resolveSnapshotFiles,
  resolveSnapshotFieldValue
} from './processFormSnapshotFieldUtils';

function ProcessMetaFields({ process, editable, onMetaChange, processType }) {
  const effectiveDateLabel = processType === 'resignation' ? '离职日期（最后工作日）' : '生效日期';
  if (!process) return null;
  if (editable) {
    return (
      <div className="process-form-meta">
        {process.process_no && (
          <Form.Item label="流程编号">
            <Input value={process.process_no} disabled />
          </Form.Item>
        )}
        {process.applicant_name && (
          <Form.Item label="申请人">
            <Input value={process.applicant_name} disabled />
          </Form.Item>
        )}
        <Form.Item label={effectiveDateLabel}>
          <DatePicker
            style={{ width: '100%' }}
            value={process.effective_date ? dayjs(process.effective_date) : null}
            onChange={(d) => onMetaChange?.({ effective_date: d?.format('YYYY-MM-DD') || null })}
          />
        </Form.Item>
        <Form.Item label="备注">
          <Input.TextArea
            rows={2}
            value={process.remark || ''}
            onChange={(e) => onMetaChange?.({ remark: e.target.value })}
          />
        </Form.Item>
      </div>
    );
  }
  return (
    <Form layout="vertical" className="process-form-readonly process-form-meta">
      {process.process_no && (
        <Form.Item label="流程编号">
          <Input value={process.process_no} readOnly />
        </Form.Item>
      )}
      {process.applicant_name && (
        <Form.Item label="申请人">
          <Input value={process.applicant_name} readOnly />
        </Form.Item>
      )}
      {process.effective_date && (
        <Form.Item label={effectiveDateLabel}>
          <DatePicker style={{ width: '100%' }} value={dayjs(process.effective_date)} disabled />
        </Form.Item>
      )}
      {process.remark && (
        <Form.Item label="备注">
          <Input.TextArea value={process.remark} rows={2} readOnly />
        </Form.Item>
      )}
    </Form>
  );
}

function TransferFormView({ formData, editable, onChange }) {
  const { subsidiaries, positions, allOrgs, loadLevels } = useHrOptions();
  const [form] = Form.useForm();
  const [levels, setLevels] = useState([]);
  const depts = allOrgs.filter((o) => o.org_type === 3 || o.org_type === 2);
  const findLabel = (list, id, key = 'org_name') => list.find((o) => o.id === id)?.[key] || id || '-';

  useEffect(() => {
    form.setFieldsValue(formData || {});
    loadLevels().then(setLevels);
  }, [form, formData, loadLevels]);

  if (!editable) {
    return (
      <Form layout="vertical" disabled className="process-form-readonly">
        <Form.Item label="员工姓名"><Input value={formData.real_name} /></Form.Item>
        <Form.Item label="新子公司"><Input value={findLabel(subsidiaries, formData.to_subsidiary_org_id)} /></Form.Item>
        <Form.Item label="新部门/项目"><Input value={findLabel(depts, formData.to_dept_org_id)} /></Form.Item>
        <Form.Item label="新岗位"><Input value={findLabel(positions, formData.to_position_id, 'label') || findLabel(positions, formData.to_position_id, 'position_name')} /></Form.Item>
        <Form.Item label="调岗原因"><Input.TextArea value={formData.reason} rows={2} /></Form.Item>
      </Form>
    );
  }

  return (
    <Form
      form={form}
      layout="vertical"
      onValuesChange={(_, all) => onChange?.({ ...formData, ...all })}
    >
      <Form.Item label="员工姓名" name="real_name"><Input disabled /></Form.Item>
      <Form.Item label="新子公司" name="to_subsidiary_org_id">
        <Select showSearch optionFilterProp="label" options={subsidiaries.map((o) => ({ value: o.id, label: o.org_name }))} />
      </Form.Item>
      <Form.Item label="新部门/项目" name="to_dept_org_id">
        <Select showSearch optionFilterProp="label" options={depts.map((o) => ({ value: o.id, label: o.org_name }))} />
      </Form.Item>
      <Form.Item label="新岗位" name="to_position_id">
        <Select
          showSearch
          optionFilterProp="label"
          options={positions.map((p) => ({ value: p.id, label: p.label || p.position_name }))}
        />
      </Form.Item>
      <Form.Item label="新职级" name="to_position_level_id">
        <Select allowClear options={levels.map((l) => ({ value: l.id, label: l.level_name }))} />
      </Form.Item>
      <Form.Item label="调岗原因" name="reason"><Input.TextArea rows={2} /></Form.Item>
    </Form>
  );
}

function RegularizationFormView({ formData, editable, onChange }) {
  const [form] = Form.useForm();
  useEffect(() => {
    const values = { ...formData };
    if (values.regularization_date) values.regularization_date = dayjs(values.regularization_date);
    form.setFieldsValue(values);
  }, [form, formData]);

  if (!editable) {
    return (
      <Form layout="vertical" disabled className="process-form-readonly">
        <Form.Item label="员工姓名"><Input value={formData.real_name} /></Form.Item>
        <Form.Item label="转正日期"><DatePicker style={{ width: '100%' }} value={formData.regularization_date ? dayjs(formData.regularization_date) : null} /></Form.Item>
        <Form.Item label="转正状态"><Select value={formData.probation_status} options={PROBATION_STATUS.map((v) => ({ value: v, label: v }))} /></Form.Item>
      </Form>
    );
  }

  return (
    <Form
      form={form}
      layout="vertical"
      onValuesChange={(_, all) => {
        const next = { ...formData, ...all };
        if (next.regularization_date?.format) {
          next.regularization_date = next.regularization_date.format('YYYY-MM-DD');
        }
        onChange?.(next);
      }}
    >
      <Form.Item label="员工姓名" name="real_name"><Input disabled /></Form.Item>
      <Form.Item label="转正日期" name="regularization_date"><DatePicker style={{ width: '100%' }} /></Form.Item>
      <Form.Item label="转正状态" name="probation_status">
        <Select options={PROBATION_STATUS.map((v) => ({ value: v, label: v }))} />
      </Form.Item>
    </Form>
  );
}

function ResignationFormView({ formData, editable, onChange }) {
  const [form] = Form.useForm();
  useEffect(() => {
    form.setFieldsValue(formData || {});
  }, [form, formData]);

  const attachmentFiles = formData.attachment_paths || [];

  if (!editable) {
    return (
      <Form layout="vertical" disabled className="process-form-readonly">
        <Form.Item label="员工姓名"><Input value={formData.real_name} /></Form.Item>
        <Form.Item label="离职类型"><Input value={formData.resign_type} /></Form.Item>
        <Form.Item label="离职原因"><Input.TextArea value={formData.reason} rows={2} /></Form.Item>
        {formData.handover && (
          <Form.Item label="交接说明"><Input.TextArea value={formData.handover} rows={2} /></Form.Item>
        )}
        {attachmentFiles.length > 0 && (
          <Form.Item label="附件">
            <SealApprovalAttachmentsView attachments={attachmentFiles} title="" />
          </Form.Item>
        )}
      </Form>
    );
  }

  return (
    <Form form={form} layout="vertical" onValuesChange={(_, all) => onChange?.({ ...formData, ...all })}>
      <Form.Item label="员工姓名" name="real_name"><Input disabled /></Form.Item>
      <Form.Item label="离职类型" name="resign_type" rules={[{ required: true, message: '请选择离职类型' }]}>
        <Select options={RESIGN_TYPE_OPTIONS} />
      </Form.Item>
      <Form.Item label="离职原因" name="reason"><Input.TextArea rows={2} /></Form.Item>
      {attachmentFiles.length > 0 && (
        <Form.Item label="附件">
          <SealApprovalAttachmentsView attachments={attachmentFiles} title="" />
        </Form.Item>
      )}
    </Form>
  );
}

const FINANCE_RESUBMIT_TYPES = new Set(['daily', 'travel', 'exp_prepayment', 'prepayment_append']);
const HR_EDITABLE_TYPES = new Set(['regularization', 'transfer', 'resignation', 'onboarding']);
// 退回发起人后可在审批中心就地编辑的财务类流程
const FINANCE_INPLACE_EDIT_TYPES = new Set([
  'fin_internal_fund_transfer',
  'budget_adjustment',
  'fin_social_fund_payment',
  'hr_social_insurance_change'
]);

const PLAN_EXTENSION_DISPLAY_FIELDS = [
  ['task_title', '事项名称'],
  ['org_code', '责任部门'],
  ['old_due_date', '变更前日期'],
  ['new_due_date', '变更后日期'],
  ['reason', '变更原因', 'textarea']
];

function buildPlanExtensionFallbackItems(formData = {}) {
  return PLAN_EXTENSION_DISPLAY_FIELDS
    .map(([key, label, type]) => {
      const value = formData[key];
      if (value == null || value === '') return null;
      return { key, label, value: String(value), type };
    })
    .filter(Boolean);
}

function FormSnapshotView({
  items = [],
  formData = {},
  processType,
  applicantPrepaymentSummary,
  preferSnapshotValues = false
}) {
  const fieldOptions = { preferFormData: !preferSnapshotValues };

  if (processType === 'fin_social_fund_payment') {
    return <SocialFundPaymentReadonlyView formData={formData} />;
  }

  if (processType === 'hr_social_insurance_change') {
    return <SocialInsuranceChangeReadonlyView formData={formData} />;
  }

  if (processType && GROUPED_FINANCE_SNAPSHOT_TYPES.has(processType)) {
    return (
      <GroupedFinanceFormSnapshotView
        items={items}
        formData={formData}
        processType={processType}
        applicantPrepaymentSummary={applicantPrepaymentSummary}
        preferSnapshotValues={preferSnapshotValues}
      />
    );
  }

  return (
    <Form layout="vertical" className="process-form-readonly process-form-snapshot">
      <Row gutter={[16, 0]}>
        {items.map((item) => {
          const fullWidth = isSnapshotFullWidthItem(item);
          const value = resolveSnapshotFieldValue(item, formData, fieldOptions);
          const valueStr = String(value);
          const isFileList = isSnapshotFileListItem(item);
          const files = isFileList ? resolveSnapshotFiles(item, formData) : [];

          return (
            <Col key={item.key || item.label} xs={24} sm={fullWidth ? 24 : 12}>
              <Form.Item label={item.label}>
                {isFileList && files.length > 0 ? (
                  <div className="process-form-snapshot-attachments">
                    <SealApprovalAttachmentsView attachments={files} title="" />
                  </div>
                ) : isSnapshotTextareaItem(item, valueStr) ? (
                  <Input.TextArea
                    className="process-form-snapshot-textarea"
                    value={valueStr}
                    readOnly
                    rows={Math.min(8, Math.max(2, valueStr.split('\n').length))}
                    autoSize={{ minRows: 2, maxRows: 8 }}
                  />
                ) : valueStr.length > 60 ? (
                  <Input.TextArea
                    className="process-form-snapshot-textarea"
                    value={valueStr}
                    readOnly
                    rows={2}
                    autoSize={{ minRows: 2, maxRows: 6 }}
                  />
                ) : (
                  <Input value={valueStr} readOnly />
                )}
              </Form.Item>
            </Col>
          );
        })}
      </Row>
    </Form>
  );
}

function OnboardingResubmitPanel({ process, formData, snapshotItems }) {
  const navigate = useNavigate();
  const employeeNo = resolveOnboardingEmployeeNo(process) || formData?.employee_no;
  const items = snapshotItems?.length ? snapshotItems : (process?.form_display || []);

  return (
    <>
      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
        message="入职申请已改用新版表单"
        description="请在入职管理中修改表单内容，保存后返回此处重新提交审批。"
        action={(
          <Button
            size="small"
            type="primary"
            disabled={!employeeNo}
            onClick={() => navigate(`/hr/onboarding/${employeeNo}/edit`)}
          >
            编辑入职信息
          </Button>
        )}
      />
      {items.length > 0 && (
        <FormSnapshotView
          items={items}
          formData={process?.form_data || formData}
          processType={process?.process_type || process?.claim_type}
          preferSnapshotValues
        />
      )}
    </>
  );
}

export default function ProcessFormView({
  processType,
  formData = {},
  process,
  formDisplay,
  editable = false,
  onFormChange,
  onMetaChange
}) {
  const data = formData || {};
  const locked = process?.status && ['pending', 'approved', 'rejected', 'cancelled'].includes(process.status)
    && !process?.awaiting_resubmit && !editable;
  const snapshotItems = formDisplay?.length ? formDisplay : (process?.form_display || []);
  const applicantPrepaymentSummary = process?.applicant_prepayment_summary;

  const [internalFundTransferForm] = Form.useForm();
  const [budgetAdjustmentForm] = Form.useForm();
  const [socialFundPaymentForm] = Form.useForm();
  const [socialInsuranceChangeForm] = Form.useForm();

  // 退回重提：进入编辑时按流程表单数据回填一次，用户后续编辑不再被回填覆盖
  const financeResubmitInitial = useMemo(() => {
    if (!editable) return undefined;
    if (!FINANCE_INPLACE_EDIT_TYPES.has(processType)) return undefined;
    return { ...(process?.form_data || data) };
  }, [process?.id, editable, processType]);

  if (locked && processType === 'company_cert_borrow') {
    return (
      <div className="process-form-view">
        <CertBorrowApplicationView
          formData={process?.form_data || data}
          snapshotItems={snapshotItems}
        />
      </div>
    );
  }

  if (locked && processType === 'company_deregistration') {
    return (
      <div className="process-form-view">
        <CompanyDeregistrationApplicationView formData={process?.form_data || data} />
      </div>
    );
  }

  if (locked && processType === 'plan_org_due_extension') {
    const items = snapshotItems.length > 0
      ? snapshotItems
      : buildPlanExtensionFallbackItems(process?.form_data || data);
    return (
      <div className="process-form-view">
        <ProcessMetaFields
          process={process}
          editable={editable}
          onMetaChange={onMetaChange}
          processType={processType}
        />
        {items.length > 0 && (
          <FormSnapshotView
            items={items}
            formData={process?.form_data || data}
            processType={processType}
            preferSnapshotValues
          />
        )}
      </div>
    );
  }

  if (locked && processType === 'onboarding') {
    return (
      <div className="process-form-view">
        {snapshotItems.length > 0 ? (
          <FormSnapshotView
            items={snapshotItems}
            formData={process?.form_data || data}
            processType={processType}
            preferSnapshotValues
          />
        ) : (
          <Form layout="vertical" className="process-form-readonly">
            <Form.Item label="提示">
              <Input value="暂无入职表单数据" readOnly />
            </Form.Item>
          </Form>
        )}
      </div>
    );
  }

  if (locked && processType === 'resignation') {
    const resignationData = {
      ...data,
      real_name: data.real_name || process?.employee_name
    };
    return (
      <div className="process-form-view">
        {snapshotItems.length > 0 ? (
          <FormSnapshotView
            items={snapshotItems}
            formData={process?.form_data || resignationData}
            processType={processType}
            preferSnapshotValues
          />
        ) : (
          <ResignationFormView formData={resignationData} editable={false} />
        )}
      </div>
    );
  }

  if (locked && snapshotItems.length > 0) {
    const isSealApproval = processType === 'contract_approval' || processType === 'seal_other_approval';
    return (
      <div className="process-form-view">
        {isSealApproval && (
          <SealApprovalAttachmentsView attachments={process?.approval_attachments} title="文件" />
        )}
        <FormSnapshotView
          items={snapshotItems}
          formData={process?.form_data || data}
          processType={processType}
          applicantPrepaymentSummary={applicantPrepaymentSummary}
          preferSnapshotValues
        />
      </div>
    );
  }

  if ((processType === 'daily' || processType === 'travel' || processType === 'exp_prepayment'
    || processType === 'exp_prepayment_writeoff' || processType === 'prepayment_append'
    || processType === 'fin_social_fund_payment' || processType === 'hr_social_insurance_change')
    && snapshotItems.length > 0 && !editable) {
    return (
      <div className="process-form-view process-form-view-finance">
        <FormSnapshotView
          items={snapshotItems}
          formData={process?.form_data || data}
          processType={processType}
          applicantPrepaymentSummary={applicantPrepaymentSummary}
          preferSnapshotValues
        />
      </div>
    );
  }

  if (editable && FINANCE_RESUBMIT_TYPES.has(processType)) {
    return (
      <div className="process-form-view process-form-view-finance">
        <FinanceProcessResubmitPanel
          processType={processType}
          process={process}
          formData={data}
          snapshotItems={snapshotItems}
          onFormChange={onFormChange}
        />
      </div>
    );
  }

  if (editable && processType === 'fin_internal_fund_transfer') {
    return (
      <div className="process-form-view process-form-view-finance">
        <InternalFundTransferForm
          form={internalFundTransferForm}
          initialValues={financeResubmitInitial}
          onValuesChange={(_, all) => onFormChange?.(all)}
        />
      </div>
    );
  }

  if (editable && processType === 'budget_adjustment') {
    return (
      <div className="process-form-view process-form-view-finance">
        <BudgetAdjustmentForm
          form={budgetAdjustmentForm}
          initialValues={financeResubmitInitial}
          onValuesChange={(_, all) => onFormChange?.(all)}
        />
      </div>
    );
  }

  if (editable && processType === 'fin_social_fund_payment') {
    return (
      <div className="process-form-view process-form-view-finance">
        <SocialFundPaymentForm
          form={socialFundPaymentForm}
          initialValues={financeResubmitInitial}
          onValuesChange={(_, all) => onFormChange?.(all)}
        />
      </div>
    );
  }

  if (editable && processType === 'hr_social_insurance_change') {
    return (
      <div className="process-form-view process-form-view-finance">
        <SocialInsuranceChangeForm
          form={socialInsuranceChangeForm}
          initialValues={financeResubmitInitial}
          onValuesChange={(_, all) => onFormChange?.(all)}
        />
      </div>
    );
  }

  if (editable && snapshotItems.length > 0 && !HR_EDITABLE_TYPES.has(processType)) {
    return (
      <div className="process-form-view">
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
          message="流程已退回"
          description="请核对下方表单快照；如需修改字段，请返回对应业务菜单或使用审批中心发起入口编辑后重新提交。"
        />
        <FormSnapshotView items={snapshotItems} formData={process?.form_data || data} processType={processType} />
      </div>
    );
  }

  return (
    <div className="process-form-view">
      {processType !== 'onboarding' && (
        <ProcessMetaFields
          process={process}
          editable={editable}
          onMetaChange={onMetaChange}
          processType={processType}
        />
      )}
      {processType === 'onboarding' && (
        editable ? (
          <OnboardingResubmitPanel
            process={process}
            formData={data}
            snapshotItems={snapshotItems}
          />
        ) : (
          snapshotItems.length > 0 ? (
            <FormSnapshotView
              items={snapshotItems}
              formData={process?.form_data || data}
              processType={processType}
              preferSnapshotValues
            />
          ) : (
            <Alert type="warning" showIcon message="暂无入职表单数据" />
          )
        )
      )}
      {processType === 'regularization' && (
        <RegularizationFormView formData={data} editable={editable} onChange={onFormChange} />
      )}
      {processType === 'transfer' && (
        <TransferFormView formData={data} editable={editable} onChange={onFormChange} />
      )}
      {processType === 'resignation' && (
        <ResignationFormView formData={data} editable={editable} onChange={onFormChange} />
      )}
    </div>
  );
}
